import { useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Users } from "@fg/icons";
import type { DriveItem } from "@/api/driveApi";
import { sharingApi } from "@/api/sharingApi";
import { useAccessToken } from "@/auth/useAccessToken";
import { DriveView, PageTitle } from "./DriveView";
import { EmptyState } from "./EmptyState";

/**
 * Files and folders other people shared with the user — in Drive's Share
 * dialog or by sending them in a Connect chat. Folders open like any folder; the item's owner,
 * role and download right travel on each row, so menus offer only what the
 * user's access allows. `?select=<id>` (the Share dialog's "Copy link")
 * selects and opens that item.
 */
export function SharedWithMePage() {
    const token = useAccessToken();
    const [params, setParams] = useSearchParams();
    const shared = useQuery({
        queryKey: ["drive", "shared-with-me", token],
        queryFn: () => sharingApi.sharedWithMe(token),
        enabled: Boolean(token),
        refetchOnWindowFocus: true
    });

    const items = useMemo<DriveItem[] | undefined>(
        () => shared.data?.map((s) => ({ ...s.item, isFavorite: false })),
        [shared.data]
    );

    const selectId = params.get("select");
    const clearSelectParam = () => {
        const next = new URLSearchParams(params);
        next.delete("select");
        next.delete("open");
        setParams(next, { replace: true });
    };

    return (
        <DriveView
            variant="shared"
            label="Shared with me"
            title={<PageTitle icon={Users}>Shared with me</PageTitle>}
            query={{
                data: items,
                isPending: shared.isPending,
                isError: shared.isError,
                error: shared.error,
                refetch: shared.refetch
            }}
            resetKey="shared"
            selectId={selectId}
            openSelected={Boolean(selectId)}
            onSelectHandled={clearSelectParam}
            emptyState={
                <EmptyState
                    icon={Users}
                    title="Nothing shared with you yet"
                    description="Files and folders people share with you — in Drive or in a Connect chat — show up here."
                />
            }
        />
    );
}
